var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import tooltipStyles from '@cof/fs-tooltip-core/lib/tooltip.lit.min.js';
import { css, html, LitElement } from 'lit';
import { property, query, state } from 'lit/decorators.js';
/**
 * `FsTooltip` implements the Financial Services implementation of the Tooltip
 * component.
 * Be sure to consult with the design guidelines for the Tooltip component to ensure proper state
 * usage, it is possible to force the Tooltip component into an unsupported state.
 *
 * ## Installation
 * ### Install the node module:
 * ```bash
 * npm i @cof/fs-tooltip-wc
 * ```
 *
 * ## Usage
 * To import the side-effectful registration of `<fs-tooltip>` call
 *
 * ```javascript
 * import '@cof/fs-tooltip-wc/fs-tooltip.js';
 * ```
 *
 * To import the `FsTooltip` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { FsTooltip } from '@cof/fs-tooltip-wc';
 * ```
 *
 * ## Anatomy
 * ### DOM Structure
 *
 * ```html
 * <fs-tooltip ?inverted=${inverted} position="${position}" size="${size}" tooltip-content="${tooltipContent}">
 *   <fs-button
 *     \@click=${() => alert(`Button clicked!`)}
 *     compact
 *     ghost
 *     icon-position="leading"
 *     type="button"
 *     variant="primary"
 *   >
 *     <fs-icon icon="${iconName}" slot="button-icon" variant="inverse"></fs-icon>
 *   </fs-button>
 * </fs-tooltip>
 * ```
 *
 * @element fs-tooltip
 * @slot - The "tool" that is being described by the tooltip.
 */
export class FsTooltip extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Disable accessibility "linking" between the tooltip and the target element. This should only be used in very
         * specific circumstances where seperate accomodations are made.
         */
        this.dangerouslyDisableAccessibilityLinking = false;
        /**
         * Disable transitions from being applied to tooltip show / hide. This is a separate control from the user's browser
         * animation config, and should only be used if there is a very specific need.
         */
        this.dangerouslyDisableTransitions = false;
        /**
         * Forcefully add an X offset to the tooltip position. This has the potential to break build-in positioning logic and
         * should only be used if you have a good understanding of what this may break.
         */
        this.dangerouslyForceXOffsetPx = 0;
        /**
         * Forcefully add a Y offset to the tooltip position. This has the potential to break build-in positioning logic and
         * should only be used if you have a good understanding of what this may break.
         */
        this.dangerouslyForceYOffsetPx = 0;
        /**
         * Whether or not the tooltip colors are "inverted".
         *
         * Should be "true" when the tooltip is intended to be placed over a dark background, "false" otherwise.
         */
        this.inverted = false;
        /**
         * Whether or not the tooltip should "flip" when its bounds are violated.
         *
         * Will only flip on the axis where the tooltip is set via `position`.
         * For example, if the tooltip position is `left`, and it will run off the screen to the left, it will automatically
         * flip to the `right`.
         */
        this.flip = true;
        /**
         * The position the tooltip should take in relationship to the tooltiped element.
         *
         * Must be one of 'top', 'bottom', 'left', 'right'.
         */
        this.position = 'top';
        /**
         * The size of the tooltip.
         *
         * Must be one of 'auto', 'small', 'large'.
         * - 'auto' should be used for short, single-line content.
         * - 'small' should be used for short content that may spill over onto the second line.
         * - 'large' should be used for larger content that may spill over onto the second line.
         */
        this.size = 'auto';
        /**
         * Slot element containing the content for the tooltip.
         */
        this.tooltipContent = '';
        /**
         * The aria attribute to be used to "link" the tooltip to the element being described by it, aria-describedby or
         * aria-labelledby.
         *
         * This should usually be left as the default aria-describedby except in the case where
         */
        this.triggerAriaAttribute = 'aria-describedby';
        /**
         * Whether or not the tooltip is visible.
         *
         * @private
         */
        this._visible = false;
        /**
         * Memo for the accessible light dom Element used for accessibility.
         *
         * @private
         */
        this._accessibleLightDomElement = null;
        /**
         * Flag enabling the component to ignore the next slot change event.
         *
         * @private
         */
        this._ignoreNextSlotChange = false;
        /**
         * How far in px the tooltip should be offset from its target.
         *
         * @private
         */
        this._tooltipOffsetFromTargetPx = 8;
        /**
         * Internal ID used for accessibility "linking".
         *
         * @private
         */
        this._tooltipId = `tooltip-${FsTooltip._instanceCount++}`;
        /**
         * Memo for the tooltip "target" element.
         *
         * @private
         */
        this._tooltipTargetElement = null;
        /**
         * Internal ID used for accessibility "linking".
         *
         * @private
         */
        this._tooltipTargetId = 'tooltip-target';
        /**
         * Hide the tooltip, update needed accessibility properties, and reset the position of the tooltip.
         *
         * @private
         * @readonly
         */
        this._hideTooltip = () => {
            // If the target element is still focused, ignore any hide that may be triggered by another mouse event.
            const rootNode = this._tooltipTargetElement.getRootNode();
            if (rootNode.activeElement === this._tooltipTargetElement) {
                return;
            }
            this._visible = false;
            this.tooltipElement.addEventListener('transitionend', this._resetTooltipPosition);
        };
        /**
         * Show the tooltip, update needed accessibility properties, and reposition the tooltip.
         *
         * @private
         * @readonly
         */
        this._showTooltip = () => {
            this._visible = true;
            this.tooltipElement.removeEventListener('transitionend', this._resetTooltipPosition);
            this._resetTooltipPosition();
            this._positionTooltip();
        };
        /**
         * Handles key up events, closes tooltip when escape key is released
         *
         * @private
         * @readonly
         */
        this._handleKeyUp = (event) => {
            if (event.code === 'Escape') {
                this._tooltipTargetElement.blur();
            }
        };
        /**
         * Position the tooltip according to the provided position, and adjust to be in viewport bounds.
         *
         * @param flipDirection The "forced" flip direction. This should usually be left null as it will recursively be set
         *                      to remediate viewport clipping issues.
         * @private
         * @readonly
         */
        this._positionTooltip = (flipDirection = null) => {
            if (this._tooltipTargetElement == null) {
                return;
            }
            const tooltipBoundingRect = this.tooltipElement.getBoundingClientRect();
            const triggerBoundingRect = this._tooltipTargetElement.getBoundingClientRect();
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;
            let tooltipOffsetX = 0;
            let tooltipOffsetY = 0;
            switch (flipDirection || this.position) {
                case 'top': {
                    // Calculate X offset.
                    // Tooltip should be horizontally centered with the trigger.
                    const triggerCenterX = triggerBoundingRect.x + triggerBoundingRect.width - triggerBoundingRect.width / 2;
                    const tooltipCenterX = tooltipBoundingRect.x + tooltipBoundingRect.width - tooltipBoundingRect.width / 2;
                    tooltipOffsetX = triggerCenterX - tooltipCenterX + this.dangerouslyForceXOffsetPx;
                    // Calculate Y offset.
                    // Tooltip should be offset above the trigger.
                    const intendedY = triggerBoundingRect.y - this._tooltipOffsetFromTargetPx - tooltipBoundingRect.height;
                    const actualY = tooltipBoundingRect.y;
                    tooltipOffsetY = intendedY - actualY + this.dangerouslyForceYOffsetPx;
                    break;
                }
                case 'bottom': {
                    // Calculate X offset.
                    // Tooltip should be horizontally centered with the trigger.
                    const triggerCenterX = triggerBoundingRect.x + triggerBoundingRect.width - triggerBoundingRect.width / 2;
                    const tooltipCenterX = tooltipBoundingRect.x + tooltipBoundingRect.width - tooltipBoundingRect.width / 2;
                    tooltipOffsetX = triggerCenterX - tooltipCenterX + this.dangerouslyForceXOffsetPx;
                    // Calculate Y offset.
                    // Tooltip should be offset below the trigger.
                    const intendedY = triggerBoundingRect.y + triggerBoundingRect.height + this._tooltipOffsetFromTargetPx;
                    const actualY = tooltipBoundingRect.y;
                    tooltipOffsetY = intendedY - actualY + this.dangerouslyForceYOffsetPx;
                    break;
                }
                case 'left': {
                    // Calculate X offset.
                    // Tooltip should be offset left of the trigger.
                    const intendedX = triggerBoundingRect.x - this._tooltipOffsetFromTargetPx - tooltipBoundingRect.width;
                    const actualX = tooltipBoundingRect.x;
                    tooltipOffsetX = intendedX - actualX + this.dangerouslyForceXOffsetPx;
                    // Calculate Y offset.
                    // Tooltip should be vertically centered with the trigger.
                    const triggerCenterY = triggerBoundingRect.y + triggerBoundingRect.height - triggerBoundingRect.height / 2;
                    const tooltipCenterY = tooltipBoundingRect.y + tooltipBoundingRect.height - tooltipBoundingRect.height / 2;
                    tooltipOffsetY = triggerCenterY - tooltipCenterY + this.dangerouslyForceYOffsetPx;
                    break;
                }
                case 'right': {
                    // Calculate X offset.
                    // Tooltip should be offset right of the trigger.
                    const intendedX = triggerBoundingRect.x + triggerBoundingRect.width + this._tooltipOffsetFromTargetPx;
                    const actualX = tooltipBoundingRect.x;
                    tooltipOffsetX = intendedX - actualX + this.dangerouslyForceXOffsetPx;
                    // Calculate Y offset.
                    // Tooltip should be vertically centered with the trigger.
                    const triggerCenterY = triggerBoundingRect.y + triggerBoundingRect.height - triggerBoundingRect.height / 2;
                    const tooltipCenterY = tooltipBoundingRect.y + tooltipBoundingRect.height - tooltipBoundingRect.height / 2;
                    tooltipOffsetY = triggerCenterY - tooltipCenterY + this.dangerouslyForceYOffsetPx;
                    break;
                }
                default: {
                    break;
                }
            }
            // Check display bounds to ensure tooltip is visible.
            // If the element will clip the right side of the screen, lock it to the right.
            if (tooltipBoundingRect.x + tooltipBoundingRect.width + tooltipOffsetX >
                windowWidth - this._tooltipOffsetFromTargetPx) {
                if (this.flip && this.position === 'right' && flipDirection == null) {
                    this._positionTooltip('left');
                    return;
                }
                const intendedX = windowWidth - this._tooltipOffsetFromTargetPx - tooltipBoundingRect.width;
                const actualX = tooltipBoundingRect.x;
                tooltipOffsetX = intendedX - actualX;
            }
            // If the element will clip the left side of the screen, lock it to the left.
            if (tooltipBoundingRect.x + tooltipOffsetX < this._tooltipOffsetFromTargetPx) {
                if (this.flip && this.position === 'left' && flipDirection == null) {
                    this._positionTooltip('right');
                    return;
                }
                const intendedX = this._tooltipOffsetFromTargetPx;
                const actualX = tooltipBoundingRect.x;
                tooltipOffsetX = intendedX - actualX;
            }
            // If the element will clip the bottom of the screen, lock it to the bottom.
            if (tooltipBoundingRect.y + tooltipBoundingRect.height + tooltipOffsetY >
                windowHeight - this._tooltipOffsetFromTargetPx) {
                if (this.flip && this.position === 'bottom' && flipDirection == null) {
                    this._positionTooltip('top');
                    return;
                }
                const intendedY = windowHeight - this._tooltipOffsetFromTargetPx - tooltipBoundingRect.height;
                const actualY = tooltipBoundingRect.y;
                tooltipOffsetY = intendedY - actualY;
            }
            // If the element will clip the top side of the screen, lock it to the top.
            if (tooltipBoundingRect.y + tooltipOffsetY < this._tooltipOffsetFromTargetPx) {
                if (this.flip && this.position === 'top' && flipDirection == null) {
                    this._positionTooltip('bottom');
                    return;
                }
                const intendedY = this._tooltipOffsetFromTargetPx;
                const actualY = tooltipBoundingRect.y;
                tooltipOffsetY = intendedY - actualY;
            }
            this.tooltipElement.style.setProperty('transform', 'translateX(' + tooltipOffsetX + 'px) translateY(' + tooltipOffsetY + 'px)');
        };
        /**
         * Reset the tooltip position to initial.
         *
         * @private
         * @readonly
         */
        this._resetTooltipPosition = () => {
            this.tooltipElement.style.setProperty('transform', 'translateX(0px) translateY(0px)');
        };
    }
    static get styles() {
        return [
            css `
        :host {
          display: inline-block;
        }
      `,
            tooltipStyles
        ];
    }
    /**
     * Remove event listeners when component is disconnected.
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        // Remove event listeners from any tooltip target.
        this._removeTooltipTargetEventListeners();
    }
    /**
     * Refreshes event listeners when component is connected.
     */
    connectedCallback() {
        super.connectedCallback();
        // Remove event listeners from any tooltip target.
        this._removeTooltipTargetEventListeners();
        // Sets event listeners to tooltip targets.
        this._setTooltipTargetEventListeners();
    }
    /**
     * Reflect styling vars as CSS custom properties.
     *
     * @protected
     */
    firstUpdated() {
        // This is handled inside an animation frame to ensure component updates (such as processing `query` are complete)
        // as is guaranteed by the framework.
        window.requestAnimationFrame(() => {
            this.tooltipElement.style.setProperty('--tooltip-offset-from-target', `${this._tooltipOffsetFromTargetPx}px`);
        });
    }
    /**
     * Called whenever the attributes of the tooltip have changed.
     * This is used to update accessibility light DOM content when the tooltip text has changed.
     *
     * @param changedProperties The properties that changed to trigger this update.
     * @protected
     */
    updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.get('tooltipContent') != null) {
            this._updateAccessibleLightDomContent();
        }
    }
    /**
     * Force the Tooltip to re-run positioning logic. This has a high performance overhead and should only be used if you
     * know what you are doing.
     */
    dangerouslyForceReposition() {
        /** DANGER: Research the effects of this in consuming elements to ensure no pop-in effect */
        if (!this.tooltipElement) {
            return;
        }
        this._resetTooltipPosition();
        this._positionTooltip();
    }
    /**
     * Render the component.
     *
     * @returns TemplateResult
     */
    render() {
        return html `
      <slot @slotchange=${this._handleTooltipTargetSlotChange}></slot>
      <div
        aria-hidden="true"
        class="
          fs-tooltip
          fs-tooltip--${this.position}
          fs-tooltip--${this.size}
          ${this.inverted ? 'fs-tooltip--inverted' : ''}
          ${this._visible ? 'fs-tooltip--visible' : ''}
          ${this.dangerouslyDisableTransitions ? 'fs-tooltip--transitions-disabled' : ''}
        "
      >
        ${this.tooltipContent}
      </div>
    `;
    }
    /**
     * Renders accessible content into the light DOM. As the tooltip itself is in the shadow DOM, it cannot be linked via
     * aria to an element in the light DOM. To compensate, the actual tooltip is ignored via aria-hidden, and this proxy
     * is inserted into the light DOM to serve as the content for a screen reader user.
     *
     * @private
     */
    _renderAccessibleLightDomContent() {
        if (this._tooltipTargetElement == null) {
            return;
        }
        if (this._accessibleLightDomElement == null) {
            this._accessibleLightDomElement = document.createElement('span');
            this._accessibleLightDomElement.textContent = this.tooltipContent;
            this._accessibleLightDomElement.id = this._tooltipId;
            this._accessibleLightDomElement.setAttribute('role', 'tooltip');
            this._accessibleLightDomElement.hidden = true;
        }
        // Ignore the next slot change as it will be triggered by this insertion to prevent an infinite loop.
        this._ignoreNextSlotChange = true;
        this._tooltipTargetElement.insertAdjacentElement('beforebegin', this._accessibleLightDomElement);
    }
    /**
     * Updates the content of the accessible light DOM content.
     *
     * @private
     */
    _updateAccessibleLightDomContent() {
        if (this._tooltipTargetElement == null) {
            return;
        }
        if (this._accessibleLightDomElement == null) {
            this._renderAccessibleLightDomContent();
            return;
        }
        this._accessibleLightDomElement.textContent = this.tooltipContent;
    }
    /**
     * Called whenever the slotted tooltip target is changed.
     * - Remove event listeners from any previous tooltip target.
     * - Memoize the new tooltip target.
     * - Update the attributes on the new tooltip target.
     * - Add event listeners to the new tooltip target.
     *
     * @private
     */
    _handleTooltipTargetSlotChange() {
        // If this slot change should be ignored, do so and reset the flag.
        if (this._ignoreNextSlotChange) {
            this._ignoreNextSlotChange = false;
            return;
        }
        // Remove any previous tooltip target element event listeners.
        this._removeTooltipTargetEventListeners();
        // Memoize the tooltip target element.
        this._memoizeTooltipTargetElement();
        // Set attributes on the target.
        this._setTooltipTargetAttributes();
        // Create a node in the light dom to reflect the accessible content.
        this._renderAccessibleLightDomContent();
        // Add event listeners to the target.
        this._setTooltipTargetEventListeners();
    }
    /**
     * Memoize the tooltip target. This allows for easier and quicker access to the element, and modification of event
     * listeners.
     *
     * @private
     */
    _memoizeTooltipTargetElement() {
        const children = this.tooltipTargetSlotElement.assignedNodes();
        // Memoize the element.
        this._tooltipTargetElement = children.find((child) => {
            return child instanceof HTMLElement;
        });
    }
    /**
     * Remove event listeners from the tooltip target.
     *
     * @private
     */
    _removeTooltipTargetEventListeners() {
        if (this._tooltipTargetElement == null) {
            return;
        }
        this._tooltipTargetElement.removeEventListener('focus', this._showTooltip);
        this._tooltipTargetElement.removeEventListener('blur', this._hideTooltip);
        this._tooltipTargetElement.removeEventListener('mouseenter', this._showTooltip);
        this._tooltipTargetElement.removeEventListener('mouseleave', this._hideTooltip);
        this._tooltipTargetElement.removeEventListener('keyup', this._handleKeyUp);
    }
    /**
     * Set needed attributes on the tooltip target.
     *
     * @private
     */
    _setTooltipTargetAttributes() {
        if (this._tooltipTargetElement == null) {
            return;
        }
        // Describe the target with the tooltip.
        if (!this.dangerouslyDisableAccessibilityLinking) {
            this._tooltipTargetElement.setAttribute(this.triggerAriaAttribute, this._tooltipId);
        }
        if (this._tooltipTargetElement.getAttribute('id') == null) {
            // If the target does not already have an ID, set one.
            this._tooltipTargetElement.setAttribute('id', this._tooltipTargetId);
        }
        else {
            // If the target does already have an ID, memoize it for internal use.
            this._tooltipTargetId = this._tooltipTargetElement.getAttribute('id');
        }
    }
    /**
     * Add event listeners to the tooltip target.
     *
     * @private
     */
    _setTooltipTargetEventListeners() {
        if (this._tooltipTargetElement == null) {
            return;
        }
        this._tooltipTargetElement.addEventListener('focus', this._showTooltip);
        this._tooltipTargetElement.addEventListener('blur', this._hideTooltip);
        this._tooltipTargetElement.addEventListener('mouseenter', this._showTooltip);
        this._tooltipTargetElement.addEventListener('mouseleave', this._hideTooltip);
        this._tooltipTargetElement.addEventListener('keyup', this._handleKeyUp);
    }
}
/** The element's canonical name for use with safe define */
FsTooltip.canonicalName = 'tooltip';
/**
 * The number of instances of a tooltip spawned. Used internally for ID uniqueness.
 *
 * @private
 */
FsTooltip._instanceCount = 0;
__decorate([
    property({ type: Boolean, attribute: 'dangerously-disable-accessibility-linking' })
], FsTooltip.prototype, "dangerouslyDisableAccessibilityLinking", void 0);
__decorate([
    property({ type: Boolean, attribute: 'dangerously-disable-transitions' })
], FsTooltip.prototype, "dangerouslyDisableTransitions", void 0);
__decorate([
    property({ type: Number, attribute: 'dangerously-force-x-offset-px' })
], FsTooltip.prototype, "dangerouslyForceXOffsetPx", void 0);
__decorate([
    property({ type: Number, attribute: 'dangerously-force-y-offset-px' })
], FsTooltip.prototype, "dangerouslyForceYOffsetPx", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsTooltip.prototype, "inverted", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsTooltip.prototype, "flip", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsTooltip.prototype, "position", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsTooltip.prototype, "size", void 0);
__decorate([
    property({ type: String, attribute: 'tooltip-content' })
], FsTooltip.prototype, "tooltipContent", void 0);
__decorate([
    property({ type: String, attribute: 'trigger-aria-attribute' })
], FsTooltip.prototype, "triggerAriaAttribute", void 0);
__decorate([
    query('div')
], FsTooltip.prototype, "tooltipElement", void 0);
__decorate([
    query('slot')
], FsTooltip.prototype, "tooltipTargetSlotElement", void 0);
__decorate([
    state()
], FsTooltip.prototype, "_visible", void 0);
//# sourceMappingURL=Tooltip.js.map